/**
 * ClarificationDialog - Phase 1.2: Conversational Clarification System
 *
 * Inline UI component for displaying clarification options within the chat.
 * Supports option buttons, custom input, and animated transitions.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, HelpCircle, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useEditorStore } from '@/stores/editor/editor-store';
import {
  FOREST_GREEN,
  BORDER,
  TEXT,
  WHITE,
  BACKGROUND,
  SHADOWS,
} from '@/styles/tokens';
import type {
  ClarificationRequest,
  ClarificationOption,
  ClarificationResponse,
} from '@/types/ai';

interface ClarificationDialogProps {
  request: ClarificationRequest;
  onResponse: (response: ClarificationResponse) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  className?: string;
}

export const ClarificationDialog: React.FC<ClarificationDialogProps> = ({
  request,
  onResponse,
  onCancel,
  isLoading = false,
  className,
}) => {
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const customInputRef = useRef<HTMLInputElement>(null);

  // Get the preview highlight setter from editor store
  const setPreviewHighlightId = useEditorStore((state) => state.setPreviewHighlightId);

  // Clear preview highlight when component unmounts or dialog closes
  useEffect(() => {
    return () => {
      setPreviewHighlightId(undefined);
    };
  }, [setPreviewHighlightId]);

  // Handle hover on option - highlight the component on canvas
  const handleOptionHover = useCallback((option: ClarificationOption, isEntering: boolean) => {
    // Check if option has component metadata (for component_selection type)
    const componentId = (option.metadata?.componentId as string | undefined) || option.id;
    if (isEntering) {
      console.log('[ClarificationDialog] Hover enter:', componentId);
      setPreviewHighlightId(componentId);
    } else {
      setPreviewHighlightId(undefined);
    }
  }, [setPreviewHighlightId]);

  console.log('[ClarificationDialog] Rendering with request:', {
    id: request.id,
    type: request.type,
    optionCount: request.options.length,
  });

  // Focus custom input when shown
  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [showCustomInput]);

  const handleOptionClick = (option: ClarificationOption) => {
    console.log('[ClarificationDialog] Option clicked:', option.id);

    // Check if this is the "custom" option
    if (option.id.includes('custom') || option.label.toLowerCase() === 'custom') {
      setShowCustomInput(true);
      return;
    }

    setSelectedOptionId(option.id);

    // Send response after brief visual feedback
    setTimeout(() => {
      onResponse({
        requestId: request.id,
        selectedOptionId: option.id,
        timestamp: new Date(),
      });
    }, 150);
  };

  const handleCustomSubmit = () => {
    if (!customValue.trim()) return;

    console.log('[ClarificationDialog] Custom value submitted:', customValue);

    onResponse({
      requestId: request.id,
      selectedOptionId: 'custom',
      customValue: customValue.trim(),
      timestamp: new Date(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCustomSubmit();
    }
    if (e.key === 'Escape') {
      if (showCustomInput) {
        setShowCustomInput(false);
        setCustomValue('');
      } else if (onCancel) {
        onCancel();
      }
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
      },
    },
  };

  const optionVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
    hover: { scale: 1.02, x: 4 },
    tap: { scale: 0.98 },
  };

  return (
    <motion.div
      className={cn('clarification-dialog', className)}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      style={{
        backgroundColor: BACKGROUND.secondary,
        borderRadius: '12px',
        border: `1px solid ${BORDER.DEFAULT}`,
        padding: '16px',
        marginTop: '8px',
        marginBottom: '8px',
        boxShadow: SHADOWS.md,
      }}
      data-testid="clarification-dialog"
    >
      {/* Question Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            backgroundColor: `${FOREST_GREEN.light}20`,
            borderRadius: '8px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <HelpCircle size={20} style={{ color: FOREST_GREEN.DEFAULT }} />
        </div>
        <div style={{ flex: 1 }}>
          <p
            style={{
              color: TEXT.primary,
              fontSize: '14px',
              fontWeight: 500,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {request.question}
          </p>
          {request.context.originalCommand && (
            <p
              style={{
                color: TEXT.secondary,
                fontSize: '12px',
                margin: '4px 0 0 0',
                fontStyle: 'italic',
              }}
            >
              For: "{request.context.originalCommand}"
            </p>
          )}
        </div>
      </div>

      {/* Options */}
      <AnimatePresence mode="wait">
        {!showCustomInput ? (
          <motion.div
            key="options"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, x: -20 }}
            variants={containerVariants}
          >
            {request.options.map((option, index) => (
              <motion.button
                key={option.id}
                variants={optionVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => handleOptionClick(option)}
                onMouseEnter={() => handleOptionHover(option, true)}
                onMouseLeave={() => handleOptionHover(option, false)}
                disabled={isLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: selectedOptionId === option.id
                    ? `${FOREST_GREEN.DEFAULT}15`
                    : WHITE,
                  border: `1px solid ${
                    selectedOptionId === option.id
                      ? FOREST_GREEN.DEFAULT
                      : option.isRecommended
                      ? FOREST_GREEN.light
                      : BORDER.DEFAULT
                  }`,
                  borderRadius: '8px',
                  cursor: isLoading ? 'wait' : 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                data-testid={`clarification-option-${index}`}
              >
                {/* Selection indicator */}
                {selectedOptionId === option.id && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      backgroundColor: FOREST_GREEN.DEFAULT,
                      borderRadius: '50%',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Check size={12} style={{ color: WHITE }} />
                  </motion.div>
                )}

                {/* Recommended badge */}
                {option.isRecommended && !selectedOptionId && (
                  <Sparkles
                    size={14}
                    style={{
                      color: FOREST_GREEN.DEFAULT,
                      position: 'absolute',
                      right: '12px',
                    }}
                  />
                )}

                <div style={{ flex: 1 }}>
                  <span
                    style={{
                      color: TEXT.primary,
                      fontSize: '14px',
                      fontWeight: option.isRecommended ? 600 : 500,
                      display: 'block',
                    }}
                  >
                    {option.label}
                    {option.isRecommended && (
                      <span
                        style={{
                          color: FOREST_GREEN.DEFAULT,
                          fontSize: '11px',
                          marginLeft: '8px',
                          fontWeight: 400,
                        }}
                      >
                        Recommended
                      </span>
                    )}
                  </span>
                  {option.description && (
                    <span
                      style={{
                        color: TEXT.secondary,
                        fontSize: '12px',
                        display: 'block',
                        marginTop: '2px',
                      }}
                    >
                      {option.description}
                    </span>
                  )}
                  {/* Preview if available */}
                  {option.preview && (
                    <span
                      style={{
                        color: FOREST_GREEN.DEFAULT,
                        fontSize: '11px',
                        display: 'block',
                        marginTop: '4px',
                      }}
                    >
                      Preview: {formatPreview(option.preview)}
                    </span>
                  )}
                </div>

                <ChevronRight
                  size={16}
                  style={{
                    color: TEXT.tertiary,
                    opacity: 0.5,
                    flexShrink: 0,
                  }}
                />
              </motion.button>
            ))}

            {/* Custom input trigger if allowed */}
            {request.allowCustomInput && (
              <motion.button
                variants={optionVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => setShowCustomInput(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: 'transparent',
                  border: `1px dashed ${BORDER.DEFAULT}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                }}
                data-testid="clarification-custom-trigger"
              >
                <span style={{ color: TEXT.secondary, fontSize: '14px' }}>
                  Other...
                </span>
              </motion.button>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="custom-input"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Input
              ref={customInputRef}
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={request.customInputPlaceholder || 'Enter custom value...'}
              disabled={isLoading}
              style={{
                fontSize: '14px',
                padding: '12px',
              }}
              data-testid="clarification-custom-input"
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomValue('');
                }}
                disabled={isLoading}
              >
                Back
              </Button>
              <Button
                size="sm"
                onClick={handleCustomSubmit}
                disabled={!customValue.trim() || isLoading}
                style={{
                  backgroundColor: FOREST_GREEN.DEFAULT,
                  color: WHITE,
                }}
              >
                Apply
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancel option */}
      {onCancel && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: `1px solid ${BORDER.light}`,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              color: TEXT.tertiary,
              fontSize: '12px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
            data-testid="clarification-cancel"
          >
            Skip this question
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

/**
 * Format a preview for display
 */
function formatPreview(preview: any): string {
  const parts: string[] = [];

  if (preview.size) {
    if (preview.size.width) parts.push(`width: ${preview.size.width}px`);
    if (preview.size.height) parts.push(`height: ${preview.size.height}px`);
  }

  if (preview.properties) {
    Object.entries(preview.properties).forEach(([key, value]) => {
      parts.push(`${key}: ${value}`);
    });
  }

  if (preview.style) {
    Object.entries(preview.style).forEach(([key, value]) => {
      parts.push(`${key}: ${value}`);
    });
  }

  return parts.join(', ') || 'Preview available';
}

export default ClarificationDialog;
